import { Row, Select, SelectOption, SelectOptionGroup } from "@amzn/meridian";
import Button from "@amzn/meridian/button";
import Input from "@amzn/meridian/input";
import React, { useState, useEffect } from "react";
import {
  getOptionLabel,
  isFieldConfirmedAnswerValid,
} from "src/components/shared/groundTruth/reviewTable/helpers";
import { INADEQUATE_INFORMATION, OTHER } from "src/constants";
import { createSearchRegExp } from "src/helpers";
import {
  GroundTruthData,
  GroundTruthField,
  ReviewResult,
  setFieldConfirmedAnswer,
} from "src/store/modelLifeCycleGroundTruthSlice";
import { FieldType } from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import { Field } from "../../../../../pages/validationWorkflow/dataProcess/dataProcessType";
import { isFieldMultiSelected } from "../../helpers";
import ChildField from "./childField";
import { getInadequateSelection, otherSelection } from "./selectionHelpers";
import Column from "@amzn/meridian/column";
import Toggle from "@amzn/meridian/toggle";

interface FullOptionsListProps {
  selectedAuditData: GroundTruthData;
  auditField: GroundTruthField;
  fieldIndex: number;
}
const FullOptionList = ({ selectedAuditData, auditField, fieldIndex }: FullOptionsListProps) => {
  const dispatch = useAppDispatch();
  const { shouldShowError } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasOverrideOptions, setHasOverrideOptions] = useState(false);
  const [useOverrideOptions, setUseOverrideOptions] = useState(false);
  const searchRegExp = createSearchRegExp(searchQuery);

  const field = Object.values(selectedModel.fields).find(
    (label: Field) => label.name === auditField.fieldName,
  );

  const many = field?.many || false;
  const isFreeTextType = field?.type === FieldType.freeText;

  useEffect(() => {
    if (field?.overrideOptions && field.overrideOptions.length > 0 && !hasOverrideOptions) {
      setHasOverrideOptions(true);
    }
  }, [field?.overrideOptions, hasOverrideOptions]);

  const fieldOptions = field?.options || [];
  const fieldOverrideOptions = field?.overrideOptions || [];
  let displayOptions = useOverrideOptions ? fieldOverrideOptions : fieldOptions;

  const hasOtherOption = fieldOptions.some((option) => compareStringsIgnoreCase(option, OTHER));

  const hasInadequateInfo = fieldOptions.some((option) =>
    compareStringsIgnoreCase(option, INADEQUATE_INFORMATION),
  );
  const isChildField = selectedModel.hierarchy?.some(
    (h) => h.childFieldName === auditField.fieldName,
  );

  let value = auditField.confirmedAnswer;

  if (many && typeof value === "string") {
    value = value.includes("|") ? value.split("|") : [value];
  }

  const clearChildFields = (parentFieldName: string) => {
    if (!selectedModel.hierarchy) return;
    const childFields = selectedModel.hierarchy
      .filter((h) => h.parentFieldName === parentFieldName)
      .map((h) => h.childFieldName);

    childFields.forEach((childFieldName) => {
      const childFieldIndex = selectedAuditData.fields.findIndex(
        (field) => field.fieldName === childFieldName,
      );

      if (childFieldIndex !== -1) {
        const childField = selectedAuditData.fields[childFieldIndex];
        const isMultiSelect = isFieldMultiSelected(childField, selectedModel);

        dispatch(
          setFieldConfirmedAnswer({
            fieldIndex: childFieldIndex,
            confirmedAnswer: isMultiSelect ? [] : "",
            reviewResult: ReviewResult.NEITHER,
          }),
        );
      }
    });
  };

  const onSelectChange = (answer: string | string[]) => {
    dispatch(
      setFieldConfirmedAnswer({
        fieldIndex: fieldIndex,
        confirmedAnswer: Array.isArray(answer) ? answer.filter((option: string) => option) : answer,
        reviewResult: ReviewResult.NEITHER,
      }),
    );
    clearChildFields(auditField.fieldName);
  };

  if (isFreeTextType) {
    return (
      <Input
        value={
          typeof value === "string"
            ? value === ReviewResult.NEITHER
              ? ""
              : value
            : value.join(",")
        }
        onChange={(answer) => {
          dispatch(
            setFieldConfirmedAnswer({
              fieldIndex: fieldIndex,
              confirmedAnswer: answer,
              reviewResult: ReviewResult.NEITHER,
            }),
          );
        }}
        type="text"
        placeholder="Enter answer..."
      />
    );
  }

  // If this is a child field, we need to handle parent-child relationship
  if (isChildField && selectedModel?.hierarchy) {
    return (
      <ChildField
        groundTruthField={auditField}
        fieldIndex={fieldIndex}
        value={value}
        hasInadequateInfo={hasInadequateInfo}
        shouldShowError={shouldShowError}
        hasOtherOption={hasOtherOption}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    );
  }

  return (
    <Row>
      <Column spacing={"100"} spacingInset={"100"}>
        {hasOverrideOptions && (
          <Toggle size="small" checked={useOverrideOptions} onChange={setUseOverrideOptions}>
            {"Load site config"}
          </Toggle>
        )}
        <Select
          value={value}
          onChange={onSelectChange}
          placeholder={"Select answer..."}
          size="small"
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          error={shouldShowError && !isFieldConfirmedAnswerValid(auditField.confirmedAnswer)}
          errorMessage={
            shouldShowError &&
            !isFieldConfirmedAnswerValid(auditField.confirmedAnswer) &&
            "please select a valid answer"
          }
        >
          {getInadequateSelection()}
          <SelectOptionGroup label={"Options"}>
            {displayOptions
              .filter((option) => searchRegExp.test(option))
              .filter((option) => !compareStringsIgnoreCase(option, INADEQUATE_INFORMATION))
              .filter((option) => !compareStringsIgnoreCase(option, OTHER))
              .map((option) => (
                <SelectOption value={option} label={option} />
              ))}
          </SelectOptionGroup>
          {hasOtherOption && otherSelection()}
        </Select>
      </Column>
      <div className="confirmedAnswerContainer">
        {Array.isArray(auditField.confirmedAnswer) &&
          auditField.confirmedAnswer.length > 1 &&
          auditField.confirmedAnswer.map((answer) => (
            <div className="confirmedAnswer">
              <Button size={"small"} type={"tertiary"}>
                {getOptionLabel(answer)}
              </Button>
            </div>
          ))}
      </div>
    </Row>
  );
};

export default FullOptionList;
